/**
 * Shared frame and primitives for the onboarding flow.
 *
 * Visual direction "Briefing" (option 1a): the client's own data is the hero
 * and chrome is nearly absent. There is no progress bar — a progress bar
 * implies a form to complete, and this flow is orientation, not a form. All
 * colour comes from the named Core theme tokens; no hex, no raw palette
 * classes.
 */

import type { ReactNode } from 'react';

/** Full-page frame. Sits outside the app shell, like the auth pages. */
export function OnboardingFrame({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-teal-light flex items-start justify-center px-4 py-10 sm:py-16">
      <div className="w-full max-w-xl bg-warm-bg border border-warm-border rounded-xl shadow-sm flex flex-col min-h-[32rem]">
        {children}
      </div>
    </div>
  );
}

export function StepBody({ children }: { children: ReactNode }) {
  return <div className="flex-1 px-6 py-7 sm:px-8 sm:py-9">{children}</div>;
}

/**
 * Action row. `secondary` is styled as a peer of `primary`, never as a
 * de-emphasised escape hatch — skipping is a first-class outcome here, and
 * hiding the skip would contradict that.
 */
export function StepFooter({ children }: { children: ReactNode }) {
  return (
    <div className="px-6 py-5 sm:px-8 border-t border-warm-border flex items-center gap-3">
      {children}
    </div>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="font-mono text-[10.5px] uppercase tracking-[0.09em] text-teal mb-3.5">
      {children}
    </p>
  );
}

/**
 * Tiny "N of M" position marker. Only ever used inside the setup job — never
 * a progress bar, never on the hub. Pairs with Eyebrow on a baseline row.
 */
export function StepCounter({ current, total }: { current: number; total: number }) {
  return (
    <p className="font-mono text-[10.5px] uppercase tracking-[0.09em] text-teal mb-3.5">
      {current} of {total}
    </p>
  );
}

export function Heading({ children }: { children: ReactNode }) {
  return (
    <h1 className="text-[25px] leading-tight font-bold tracking-tight text-foreground mb-2.5">
      {children}
    </h1>
  );
}

export function Lede({ children }: { children: ReactNode }) {
  return <p className="text-sm leading-relaxed text-teal-dark mb-4">{children}</p>;
}

export function Note({ children }: { children: ReactNode }) {
  return <p className="text-xs leading-relaxed text-teal">{children}</p>;
}

export function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string;
  children: ReactNode;
}) {
  const className = 'block text-[11.5px] font-semibold text-teal-dark mb-1.5';
  if (htmlFor) {
    return (
      <label htmlFor={htmlFor} className={className}>
        {children}
      </label>
    );
  }
  return <span className={className}>{children}</span>;
}

/** One key/value line on the recognition screen. */
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3.5 py-2.5 border-b border-warm-border last:border-b-0">
      <span className="font-mono text-[11.5px] leading-relaxed text-teal w-[4.6rem] shrink-0">
        {label}
      </span>
      <span className="text-[13px] leading-relaxed font-medium text-foreground">{children}</span>
    </div>
  );
}

type ButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
};

export function PrimaryButton({ children, onClick, disabled, type = 'button' }: ButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-primary-action
      className="flex-1 rounded-lg bg-coral px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-coral-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, onClick, disabled }: ButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="shrink-0 rounded-lg border border-warm-border-strong px-4 py-3 text-sm font-medium text-teal transition-colors hover:text-teal-dark hover:border-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
    >
      {children}
    </button>
  );
}

export function QuietLink({ children, onClick, href }: ButtonProps & { href?: string }) {
  const className =
    'mx-auto text-[13px] font-medium text-teal underline underline-offset-4 hover:text-teal-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded';
  if (href) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {children}
    </button>
  );
}

/** Status line. Optional `onClick` wraps the row as a button (hub jobs).
 *  `locked` is a purely visual disabled state: the row dims, drops its
 *  hover/focus affordances, and never renders as a button. Defaults false so
 *  the wire agent can opt in per job. */
export function StatusRow({
  tone,
  title,
  detail,
  onClick,
  locked = false,
}: {
  tone: 'done' | 'pending';
  title: string;
  detail: string;
  onClick?: () => void;
  locked?: boolean;
}) {
  const className = [
    'flex gap-3 items-start p-3.5 border border-warm-border rounded-lg mb-2.5 w-full text-left',
    locked
      ? 'opacity-60 cursor-not-allowed'
      : onClick
        ? 'transition-colors hover:border-warm-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        : '',
  ].join(' ');
  const inner = (
    <>
      <span
        aria-hidden
        className={[
          'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
          tone === 'done'
            ? 'bg-[hsl(var(--status-success))] ring-4 ring-[hsl(var(--status-success)/0.12)]'
            : 'bg-coral ring-4 ring-coral-light',
        ].join(' ')}
      />
      <div>
        <p className="text-[13px] font-semibold leading-snug text-foreground mb-0.5">{title}</p>
        <p className="text-xs leading-relaxed text-teal">{detail}</p>
      </div>
    </>
  );
  if (onClick && !locked) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {inner}
      </button>
    );
  }
  return (
    <div className={className} aria-disabled={locked || undefined}>
      {inner}
    </div>
  );
}
